// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type {
  Attributes,
  QdsFocusEventDetail,
  QdsInputEventDetail,
} from '../../helpers'
import {
  inheritAriaAttributes,
  invariant,
  pickFocusEventAttributes,
  pickInputEventAttributes,
} from '../../helpers'
import type { AutoComplete } from '../controls'
import { CUSTOM_ERROR_FLAGS, NO_ERROR_FLAGS, VALID_STATE } from '../controls'
import type { Size } from '../shared'

export type SelectType = 'select-multiple' | 'select-one'

const throwError = (): never => {
  invariant(false)
}
const EMPTY_OPTIONS_COLLECTION: HTMLSelectElement['options'] = {
  length: 0,
  selectedIndex: -1,
  add: throwError,
  remove: throwError,
  item: throwError,
  namedItem: throwError,
  [Symbol.iterator]: throwError,
}

/**
 * The `<qds-select>` element represents a control that provides a menu of
 * options.
 *
 * @see https://quartz.se.com/build/components/select
 */
@Component({
  tag: 'qds-select',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'select.css',
})
export class Select implements ComponentInterface {
  /**
   * The select's size.
   *
   * > ***NOTE:*** The native [`size` HTML attribute] is not supported. CSS
   * `height` should be used instead if this functionality is needed.
   *
   * [`size` HTML attribute]: https://developer.mozilla.org/docs/Web/HTML/Attributes/size
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Provides a hint for a [user agent's][] autocomplete feature. See
   * [the HTML `autocomplete` attribute][] for a complete list of values and
   * details on how to use `autoComplete`.
   *
   * [user agent's]: https://developer.mozilla.org/docs/Glossary/User_agent
   * [the HTML `autocomplete` attribute]: https://developer.mozilla.org/docs/Web/HTML/Attributes/autocomplete
   *
   * @webnative
   */
  @Prop({ attribute: 'autocomplete' })
  public readonly autoComplete?: AutoComplete

  /**
   * Specify whether the select should have focus when the page loads.
   *
   * @webnative
   */
  @Prop({ attribute: 'autofocus' })
  public readonly autoFocus?: boolean

  /**
   * Prevents the select from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop() public readonly disabled?: boolean

  /**
   * The [`<form>`][] element to associate the select with (its form owner).
   *
   * The value of this attribute must be the id of a `<form>` in the same
   * document. If this attribute is not set, the `<qds-select>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This attribute lets you associate `<qds-select>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * [`<form>`]: https://developer.mozilla.org/docs/Web/HTML/Element/form
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * Returns the number of elements in the [`<option>`s collection][].
   *
   * [`<option>`s collection]: https://developer.mozilla.org/docs/Web/API/HTMLOptionsCollection
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly length: HTMLSelectElement['length'] = 0

  /**
   * Indicates that multiple options can be selected in the list. If it is not
   * specified, then only one option can be selected at a time. When `multiple`
   * is specified, most browsers will show a scrolling list box instead of a
   * single line dropdown.
   *
   * @webnative
   */
  @Prop() public multiple?: boolean

  /**
   * The name of the select, which is submitted with the form data.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * An [`HTMLOptionsCollection`][] of the [`<option>`][] elements contained by
   * the `<qds-select>` element.
   *
   * [`HTMLOptionsCollection`]: https://developer.mozilla.org/docs/Web/API/HTMLOptionsCollection
   * [`<option>`]: https://developer.mozilla.org/docs/Web/HTML/Element/option
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly options: HTMLSelectElement['options'] =
    EMPTY_OPTIONS_COLLECTION

  /**
   * An `<option>` with a non-empty string value must be selected before this
   * select can be submitted.
   *
   * See [Client-side validation] and the [HTML attribute: `required`] for more
   * information.
   *
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   * [HTML attribute: `required`]: https://developer.mozilla.org/docs/Web/HTML/Attributes/required
   *
   * @webnative
   */
  @Prop() public readonly required?: boolean

  /**
   * The index of the first or last selected [`<option>`][] element, depending
   * on the value of `multiple`. The value `-1` indicates that no element is
   * selected.
   *
   * [`<option>`]: https://developer.mozilla.org/docs/Web/HTML/Element/option
   *
   * @webnative
   */
  @Prop({ mutable: true })
  public selectedIndex: HTMLSelectElement['selectedIndex'] = -1

  /**
   * Collection of the [`<option>`][] elements contained within the
   * `<qds-select>` element that are currently selected.
   *
   * [`<option>`]: https://developer.mozilla.org/docs/Web/HTML/Element/option
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly selectedOptions: HTMLSelectElement['selectedOptions'] =
    EMPTY_OPTIONS_COLLECTION

  /**
   * Returns `select-multiple` if the `multiple` attribute is true;
   * `select-one` otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly type: SelectType = 'select-one'

  /**
   * The error message that would be shown to the user if the `<qds-select>`
   * was to be checked for validity.
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly validationMessage: ElementInternals['validationMessage'] = ''

  /**
   * The [`ValidityState`][] object for this `<qds-select>`.
   *
   * [`ValidityState`]: https://developer.mozilla.org/docs/Web/API/ValidityState
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly validity: ElementInternals['validity'] = VALID_STATE

  /**
   * The `value` property of the first selected [`<option>`][] element, or the
   * empty string if no options are selected.
   *
   * [`<option>`]: https://developer.mozilla.org/docs/Web/HTML/Element/option
   *
   * @webnative
   */
  @Prop({ mutable: true }) public value? = ''

  /**
   * True if `<qds-select>` will be validated when the form is submitted;
   * false otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly willValidate: ElementInternals['willValidate'] = false

  @AttachInternals() private readonly internals!: ElementInternals

  /**
   * Emitted when the select loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when an alteration to the select's value is committed by the
   * user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the select gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the select's value changes.
   */
  @Event({ eventName: 'qdsInput', cancelable: false })
  private readonly inputEmitter!: EventEmitter<QdsInputEventDetail>

  @Element() private readonly host!: HTMLQdsSelectElement

  @State() private invalid = false

  @State() private tabIndex?: number

  #inheritedAttributes: Attributes = {}

  #select?: HTMLSelectElement

  get #computedDisabled(): boolean {
    return (
      (this.host.matches(':disabled') || (this.disabled ?? false)) &&
      this.host.getAttribute('disabled') !== 'false'
    )
  }

  get #computedOptions(): HTMLSelectElement['options'] {
    invariant(this.#select)

    return this.#select.options
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedType(): HTMLSelectElement['type'] {
    invariant(this.#select)

    return this.#select.type
  }

  get #computedValue(): string {
    return this.value ?? ''
  }

  get #computedSelectedOptions(): HTMLSelectElement['selectedOptions'] {
    invariant(this.#select)

    return this.#select.selectedOptions
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) event.stopImmediatePropagation()
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    if (this.#computedDisabled)
      // eslint-disable-next-line unicorn/no-null
      this.internals.setFormValue(null)
    else this.internals.setFormValue(this.#computedValue)
  }

  @Watch('selectedIndex')
  protected selectedIndexChanged(): void {
    invariant(this.#select)

    this.#select.selectedIndex = this.selectedIndex
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('required')
  @Watch('value')
  protected validationChanged(): void {
    invariant(this.#select)

    this.#select.value = this.#computedValue
    this.#select.required = this.required ?? false

    const { valid } = this.#select.validity
    if (valid)
      this.internals.setValidity(NO_ERROR_FLAGS, undefined, this.#select)
    else
      this.internals.setValidity(
        this.#select.validity,
        this.#select.validationMessage,
        this.#select,
      )
    this.invalid = !valid
  }

  public componentWillLoad(): void {
    this.#defineGetter('form', () => this.internals.form)
    this.#defineGetter('willValidate', () => this.internals.willValidate)
    this.#defineGetter('validity', () => this.internals.validity)
    this.#defineGetter(
      'validationMessage',
      () => this.internals.validationMessage,
    )

    this.#defineGetter('type', () => this.#computedType)
    this.#defineGetter('options', () => this.#computedOptions)
    this.#defineGetter('length', () => this.#computedOptions.length)
    this.#defineGetter('selectedOptions', () => this.#computedSelectedOptions)

    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public componentDidLoad(): void {
    invariant(this.#select)

    this.validationChanged()
    this.selectedIndex = this.#select.selectedIndex
  }

  public render() {
    return (
      <div
        class={{
          'qds-container': true,
          'qds-multiple': this.multiple ?? false,
        }}
        data-size={this.#computedSize}
      >
        <select
          aria-invalid={this.invalid.toString()}
          autoComplete={this.autoComplete}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={this.autoFocus}
          class="qds-select"
          data-size={this.#computedSize}
          disabled={this.#computedDisabled}
          multiple={this.multiple}
          name={this.name}
          onBlur={this.#onBlur}
          onChange={this.#onChange}
          onFocus={this.#onFocus}
          onInput={this.#onInput}
          ref={this.#ref}
          required={this.required}
          tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.#inheritedAttributes}
        />
        {this.multiple !== true && (
          <qds-icon
            aria-hidden="true"
            class="qds-icon"
            data-size={this.#computedSize}
            library="core"
            name="dropdown"
          />
        )}
        <slot onSlotchange={this.#onSlotchange} />
        <div class="qds-focus-ring" />
      </div>
    )
  }

  public checkValidity: ElementInternals['checkValidity'] = () =>
    this.internals.checkValidity()

  public reportValidity: ElementInternals['reportValidity'] = () =>
    this.internals.reportValidity()

  public setCustomValidity: HTMLInputElement['setCustomValidity'] = (error) => {
    if (error)
      this.internals.setValidity(CUSTOM_ERROR_FLAGS, error, this.#select)
    else this.internals.setValidity(NO_ERROR_FLAGS)
    this.invalid = !!error
  }

  public addItem: HTMLSelectElement['add'] = (element, before) => {
    invariant(this.#select)

    this.#select.add(element, before)
  }

  public removeItem = (index: number): void => {
    invariant(this.#select)

    this.#select.remove(index)
  }

  public item: HTMLSelectElement['item'] = (index) => {
    invariant(this.#select)

    return this.#select.item(index)
  }

  public namedItem: HTMLSelectElement['namedItem'] = (name) => {
    invariant(this.#select)

    return this.#select.namedItem(name)
  }

  readonly #ref = (select?: HTMLSelectElement): void => {
    this.#select = select
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onChange = (): void => {
    this.changeEmitter.emit()
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onInput = (event: InputEvent): void => {
    invariant(this.#select)

    this.value = this.#select.value
    this.inputEmitter.emit(pickInputEventAttributes(event))
  }

  readonly #onSlotchange = (event: Event): void => {
    invariant(this.#select)
    const { currentTarget } = event
    invariant(currentTarget instanceof HTMLSlotElement)

    this.#select.replaceChildren(
      ...currentTarget.assignedNodes().map((node) => node.cloneNode(true)),
    )
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }
}
